import React, { useEffect, useState } from "react";
import { FaEye, FaShieldAlt } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import profilepic from "../../assests/modalpic.png";
import userServices from "../../api/services/userService";
import Loading from "../../components/Loading";

const TopUsers = () => {
  const [activeTab, setActiveTab] = useState("all");
  const [isOpen, setIsOpen] = useState(false);
  const [users, setUsers] = useState([]);
  const [backendPage, setBackendPage] = useState(1); // backend page
  const [total, setTotal] = useState(0); // total users from backend
  const backendLimit = 15; // backend returns 15 users per page
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const navigate = useNavigate();

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      setBackendPage(1); // Reset to first page when search changes
    }, 500);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setIsLoading(true);
        let result;

        if (activeTab === "all") {
          result = await userServices.fetchAllUsers(
            backendPage,
            debouncedSearch || null
          );
        } else if (activeTab === "completed") {
          result = await userServices.fetchCompletedUsers(
            backendPage,
            debouncedSearch || null
          );
        }

        console.log(result);
        const allUsers = result?.users || [];
        setUsers(allUsers);
        setTotal(result?.total || 0);
      } catch (error) {
        console.error("Error fetching users:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchUsers();
  }, [backendPage, activeTab, debouncedSearch]);

  const tabs = [
    { key: "all", label: "Recent Users" },
    { key: "completed", label: "Completed Users" },
  ];

  // Calculate pagination
  const totalPages = Math.ceil(total / backendLimit);

  // Since backend limit is now 15 (same as frontend), we can simplify
  // Just use backendPage for pagination
  const currentPage = backendPage;
  const startIndex = (currentPage - 1) * backendLimit + 1;
  const endIndex = Math.min(currentPage * backendLimit, total);

  const handleNext = () => {
    if (currentPage < totalPages) {
      setBackendPage((prev) => prev + 1);
    }
  };

  const handlePrev = () => {
    if (currentPage > 1) {
      setBackendPage((prev) => prev - 1);
    }
  };

  // Format date (match subscription start date format: date only)
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <div className="p-4 w-full">
      {isLoading ? (
        <Loading />
      ) : (
        <>
          {/* Tabs and Filters */}
          <div className="flex justify-between items-center mb-4 flex-wrap gap-4">
            <div className="flex gap-4">
              {tabs.map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => {
                    setActiveTab(tab.key);
                    setBackendPage(1);
                  }}
                  className={`px-4 py-2 rounded ${
                    activeTab === tab.key
                      ? "bg-red-500 text-white shadow text-xs md:text-lg"
                      : "bg-gray-100 text-gray-700 text-xs md:text-lg"
                  }`}>
                  {tab.label}
                </button>
              ))}
            </div>

            <div className="flex gap-2 flex-wrap items-center">
              <input
                type="text"
                placeholder="Search by name or email..."
                className="border px-3 py-2 rounded w-64 text-xs md:text-lg"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto bg-white shadow rounded-lg">
            <table className="w-full text-left border-collapse">
              <thead className="bg-gray-100">
                <tr>
                  <th className="px-4 py-2 text-xs md:text-lg">SR. NO</th>
                  <th className="px-4 py-2 text-xs md:text-lg">First Name</th>
                  <th className="px-4 py-2 text-xs md:text-lg">Last Name</th>
                  <th className="px-4 py-2 text-xs md:text-lg">Email</th>
                  <th className="px-4 py-2 text-xs md:text-lg">
                    Contact Number
                  </th>
                  <th className="px-4 py-2 text-xs md:text-lg">
                    Created Date
                  </th>
                  <th className="px-4 py-2 text-xs md:text-lg">
                    DOCUMENT STATUS
                  </th>
                  <th className="px-4 py-2 text-xs md:text-lg">ACTIONS</th>
                </tr>
              </thead>
              <tbody>
                {users.length > 0 ? (
                  users.map((user, idx) => (
                    <tr key={user.id} className="border-t">
                      <td className="px-4 py-2 text-xs md:text-lg">
                        {(backendPage - 1) * backendLimit + idx + 1}
                      </td>
                      <td className="px-4 py-2 flex items-center gap-2 text-xs md:text-lg">
                        {user.firstName}
                      </td>
                      <td className="px-4 py-2 text-xs md:text-lg">
                        {user.lastName}
                      </td>
                      <td className="px-4 py-2 text-xs md:text-lg">
                        {user.email}
                      </td>
                      <td className="px-4 py-2 text-xs md:text-lg">
                        {user.contactNo}
                      </td>
                      <td className="px-4 py-2 text-xs md:text-lg">
                        {user.createdDate ? formatDate(user.createdDate) : "N/A"}
                      </td>
                      <td
                        className={`px-4 py-2 font-semibold text-xs md:text-lg ${
                          user.document === "Not Uploaded"
                            ? "text-red-500"
                            : "text-green-500"
                        }`}>
                        {user.document || "Not Uploaded"}
                      </td>
                      <td className="px-4 py-2 flex gap-3 text-xs md:text-lg">
                        <div
                          className="relative group"
                          onClick={() => navigate(`/userdetails/${user.id}`)}>
                          <FaEye className="text-red-500 cursor-pointer" />
                          <span className="absolute bottom-full mb-2 hidden group-hover:block bg-gray-700 text-white text-xs px-2 py-1 rounded">
                            View
                          </span>
                        </div>

                        <div className="relative group">
                          <FaShieldAlt
                            className="text-red-500 cursor-pointer"
                            onClick={() => setIsOpen(true)}
                          />
                          <span className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 hidden group-hover:block bg-gray-700 text-white text-xs text-center px-2 py-1 rounded w-[135px] z-50">
                            Verify User Profile Pic
                          </span>
                        </div>
                        {isOpen && (
                          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                            <div className="bg-white rounded-lg shadow-lg w-4/3 h-auto p-6 relative">
                              <button
                                className="absolute top-2 right-2 text-gray-500 hover:text-black"
                                onClick={() => setIsOpen(false)}>
                                ✕
                              </button>
                              <h2 className="text-xl font-bold mb-4">
                                Verify User Profile Pic
                              </h2>
                              <div className="w-full h-80">
                                <img src={profilepic} alt="" width={300} />
                              </div>

                              <div className="flex justify-end gap-2">
                                <button
                                  onClick={() => setIsOpen(false)}
                                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400">
                                  Cancel
                                </button>
                                <button className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600">
                                  Approve
                                </button>
                                <button className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600">
                                  Reject
                                </button>
                              </div>
                            </div>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan="8"
                      className="text-center py-4 text-gray-500 font-medium">
                      No users found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="mt-6 flex flex-col sm:flex-row items-center justify-between gap-4 px-4 py-4 bg-gray-50 rounded-lg border border-gray-200">
              <div className="text-xs md:text-sm font-medium text-gray-700">
                Showing page{" "}
                <span className="font-bold text-gray-900">{currentPage}</span>{" "}
                of <span className="font-bold text-gray-900">{totalPages}</span>{" "}
                (Total: <span className="font-bold text-red-600">{total}</span>{" "}
                users)
              </div>
              <div className="flex gap-2 items-center justify-center">
                <button
                  onClick={handlePrev}
                  disabled={currentPage === 1}
                  className={`px-4 py-2 border rounded-lg font-medium text-xs md:text-sm transition-colors ${
                    currentPage === 1
                      ? "bg-gray-100 text-gray-400 cursor-not-allowed border-gray-200"
                      : "bg-white text-gray-700 hover:bg-gray-50 border-gray-300 hover:border-gray-400"
                  }`}>
                  Previous
                </button>
                <span className="text-xs md:text-sm text-gray-700">
                  <span className="font-bold">{startIndex}</span> -{" "}
                  <span className="font-bold">{endIndex}</span> of{" "}
                  <span className="font-bold text-red-600">{total}</span>
                </span>
                <button
                  onClick={handleNext}
                  disabled={currentPage === totalPages}
                  className={`px-4 py-2 border rounded-lg font-medium text-xs md:text-sm transition-colors ${
                    currentPage === totalPages
                      ? "bg-gray-100 text-gray-400 cursor-not-allowed border-gray-200"
                      : "bg-white text-gray-700 hover:bg-gray-50 border-gray-300 hover:border-gray-400"
                  }`}>
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default TopUsers;
